// The assistant eval console's decisions (ASSISTANT_EVAL_CONSOLE_PLAN.md, phases 7 and 8).
//
// Pure: no Nuxt, no fetch, no timers of its own (the poller takes them as arguments), so
// every rule the page relies on is tested in tests/unit/assistant-evals.test.ts. The page
// and its tab components only wire these to the API.
//
// Shapes follow SLL.Api's AssistantEvalsController: camelCase, null fields OMITTED (so
// compare with == null), enums by name. Run statuses arrive as "Running", verdicts as
// "bot_wrong". Nothing here may contain an em or en dash: it is all copy a reader sees.

// ---- run status -----------------------------------------------------------------------

const STATUSES = ['Queued', 'Running', 'Completed', 'Cancelled', 'Interrupted', 'StoppedAtBudget', 'Failed'];

const STATUS_LABELS = {
  Queued: 'Queued',
  Running: 'Running',
  Completed: 'Finished',
  Cancelled: 'Cancelled',
  Interrupted: 'Interrupted',
  StoppedAtBudget: 'Stopped at budget',
  Failed: 'Failed',
};

/** The canonical status name, whatever case the API used; null when there is none. */
export function normaliseStatus(status) {
  if (status == null) return null;
  const text = String(status).toLowerCase();
  return STATUSES.find(s => s.toLowerCase() === text) ?? String(status);
}

export function runStatusLabel(status) {
  const s = normaliseStatus(status);
  return STATUS_LABELS[s] ?? s ?? 'Unknown';
}

/** Still going: the page keeps polling, and Cancel is offered. */
export function isRunActive(status) {
  const s = normaliseStatus(status);
  return s === 'Queued' || s === 'Running';
}

export const canCancelRun = isRunActive;

/** The API resumes anything that stopped short; a finished run is started again instead. */
export function canResumeRun(status) {
  const s = normaliseStatus(status);
  return s === 'Interrupted' || s === 'Cancelled' || s === 'StoppedAtBudget' || s === 'Failed';
}

// ---- polling --------------------------------------------------------------------------

/** Given the latest GET runs/{id}, whether to poll again. */
export function pollDecision(detail) {
  return isRunActive(detail?.run?.status) ? 'continue' : 'stop';
}

/**
 * Polls `load` every `intervalMs` until the run stops or `maxErrors` loads fail in a row.
 * One poll in flight at a time: the next is only scheduled once the last has answered, so
 * a slow API never stacks requests. `schedule`/`cancel` are injectable for tests.
 */
export function createPoller({
  load,
  onResult,
  onError,
  intervalMs = 2000,
  maxErrors = 5,
  schedule = (fn, ms) => setTimeout(fn, ms),
  cancel = (id) => clearTimeout(id),
}) {
  let stopped = true;
  let timer = null;
  let errors = 0;

  const tick = async () => {
    timer = null;
    if (stopped) return;
    try {
      const detail = await load();
      errors = 0;
      if (stopped) return;
      onResult?.(detail);
      if (pollDecision(detail) === 'stop') {
        stopped = true;
        return;
      }
    } catch (e) {
      errors += 1;
      onError?.(e, errors);
      if (errors >= maxErrors) {
        stopped = true;
        return;
      }
    }
    if (!stopped) timer = schedule(tick, intervalMs);
  };

  return {
    async start() {
      if (!stopped) return;
      stopped = false;
      errors = 0;
      await tick();
    },
    stop() {
      stopped = true;
      if (timer != null) cancel(timer);
      timer = null;
    },
    get running() {
      return !stopped;
    },
  };
}

// ---- verdicts and result groups -------------------------------------------------------

const VERDICT_LABELS = {
  pass: 'Pass',
  bot_wrong: 'Bot wrong',
  case_stale: 'Case stale',
  docs_problem: 'Docs problem',
  judge_unsure: 'Judge unsure',
};

export function verdictLabel(verdict) {
  if (verdict == null) return 'No verdict';
  return VERDICT_LABELS[String(verdict).toLowerCase()] ?? String(verdict);
}

/** Worst first: what needs a person reads top down. */
export const RESULT_GROUPS = [
  {key: 'botWrong', label: 'Bot wrong', color: 'error'},
  {key: 'unsure', label: 'Unsure', color: 'warning'},
  {key: 'stale', label: 'Stale', color: 'warning'},
  {key: 'docsProblem', label: 'Docs problem', color: 'warning'},
  {key: 'flaky', label: 'Flaky', color: 'info'},
  {key: 'passWithWarnings', label: 'Pass with style warnings', color: 'success'},
  {key: 'pass', label: 'Pass', color: 'success'},
];

/**
 * The one group a result belongs in. The API's outcome decides fail and not scored, so
 * the page can never count a case differently from the gates.
 */
export function resultGroupOf(result) {
  const outcome = String(result?.outcome ?? '').toLowerCase();
  const verdict = String(result?.verdict ?? '').toLowerCase();
  if (outcome === 'fail') return 'botWrong';
  if (outcome === 'notscored') {
    if (verdict === 'case_stale') return 'stale';
    if (verdict === 'docs_problem') return 'docsProblem';
    return 'unsure';
  }
  if (result?.flaky) return 'flaky';
  if (result?.styleWarnings?.length) return 'passWithWarnings';
  return 'pass';
}

/** Every group in order, each with the results that count (a rerun stands over its first try). */
export function groupResults(results) {
  const finals = (results ?? []).filter(r => r.final !== false);
  return RESULT_GROUPS.map(group => ({
    ...group,
    results: finals.filter(r => resultGroupOf(r) === group.key),
  }));
}

// ---- numbers --------------------------------------------------------------------------

/** Dollars: pennies to two places, fractions of a penny to four. Null reads as unknown. */
export function usd(value, unknown = 'unknown') {
  if (value == null || value === '') return unknown;
  const n = Number(value);
  if (!Number.isFinite(n)) return unknown;
  const places = n !== 0 && Math.abs(n) < 0.01 ? 4 : 2;
  return '$' + n.toFixed(places);
}

const percent = (fraction) => {
  const n = Math.round(Number(fraction) * 1000) / 10;
  return (Number.isInteger(n) ? String(n) : n.toFixed(1)) + '%';
};

const plural = (n, one, many = one + 's') => `${n} ${n === 1 ? one : many}`;

const KIND_LABELS = {
  howto: 'How to',
  'refuse-noanswer': 'Refuse, no answer',
  'refuse-offtopic': 'Refuse, off topic',
  injection: 'Injection',
  feedback: 'Feedback',
  'cross-platform': 'Cross platform',
};

export function kindLabel(kind) {
  return KIND_LABELS[kind] ?? kind ?? 'Unknown';
}

const PLATFORM_LABELS = {app: 'App', web: 'Website', both: 'Both'};

export function platformLabel(platform) {
  return PLATFORM_LABELS[platform] ?? platform ?? '';
}

// ---- score line and gates -------------------------------------------------------------

/** One line a person can read at a glance, and paste into a message. */
export function scoreLine(run) {
  if (!run) return '';
  const parts = [];
  const gates = run.gates ?? [];
  if (run.score == null) {
    parts.push('Nothing scored yet');
  } else {
    parts.push(percent(run.score));
    if (gates.length) {
      const passed = gates.reduce((sum, g) => sum + (g.passed ?? 0), 0);
      const scored = gates.reduce((sum, g) => sum + (g.scored ?? 0), 0);
      const notScored = gates.reduce((sum, g) => sum + (g.notScored ?? 0), 0);
      parts.push(`${passed} of ${scored} passed`);
      if (notScored) parts.push(`${notScored} not scored`);
    }
  }
  if (run.gatesHeld === true) parts.push('gates held');
  if (run.gatesHeld === false) parts.push('gates missed');
  if (run.styleWarningCount) parts.push(plural(run.styleWarningCount, 'style warning'));
  if (run.medianReplySeconds != null) parts.push(`median reply ${run.medianReplySeconds} s`);
  if (run.rateLimitedCount) parts.push(`${run.rateLimitedCount} rate limited`);
  parts.push(usd(run.totalCostUsd ?? 0));
  return parts.join(' · ');
}

export function gateRows(gates) {
  return (gates ?? []).map(g => ({
    kind: g.kind,
    label: kindLabel(g.kind),
    passed: g.passed ?? 0,
    scored: g.scored ?? 0,
    notScored: g.notScored ?? 0,
    share: g.scored ? percent((g.passed ?? 0) / g.scored) : 'nothing scored',
    threshold: percent(g.threshold ?? 0),
    state: g.held === true ? 'held' : g.held === false ? 'missed' : 'none',
  }));
}

export function progressText(run) {
  const done = run?.completedCount ?? 0;
  const planned = run?.plannedCount ?? 0;
  if (!planned) return `${done} answered`;
  return `${done} of ${planned} answered (${Math.round((done / planned) * 100)}%)`;
}

export function progressPercent(run) {
  const planned = run?.plannedCount ?? 0;
  return planned ? Math.min(100, ((run?.completedCount ?? 0) / planned) * 100) : 0;
}

/** "published at 0123456", "branch docs/pass at abc1234", or "not used" for a platform the run left out. */
export function docsSourceText(source, sha) {
  if (!source) return 'not used';
  const [kind, ...rest] = String(source).split(':');
  const ref = rest.join(':');
  const text = ref ? `${kind} ${ref}` : kind;
  return sha ? `${text} at ${String(sha).slice(0, 7)}` : text;
}

// ---- spend and the estimate -----------------------------------------------------------

export function spendRows(status) {
  if (!status) return [];
  const split = (s) => `bot ${usd(s?.botUsd ?? 0)} · judge ${usd(s?.judgeUsd ?? 0)}`;
  return [
    {
      label: 'Evals today',
      value: usd(status.evalSpendToday?.totalUsd ?? 0),
      detail: `${split(status.evalSpendToday)} · budget ${usd(status.maxDailySpendUsd ?? 0)}`,
    },
    {
      label: 'Evals this month',
      value: usd(status.evalSpendThisMonth?.totalUsd ?? 0),
      detail: `${split(status.evalSpendThisMonth)} · budget ${usd(status.maxMonthlySpendUsd ?? 0)}`,
    },
    {
      label: 'Evals all time',
      value: usd(status.evalSpendAllTime?.totalUsd ?? 0),
      detail: split(status.evalSpendAllTime),
    },
    {
      label: 'Live assistant this month',
      value: usd(status.liveAssistantSpendThisMonthUsd ?? 0),
      detail: 'what members used',
    },
  ];
}

/** What POST estimate says, in the order a person decides on it: cost, where it comes from, then budgets. */
export function estimateView(estimate) {
  if (!estimate) return null;
  const platforms = (estimate.platforms ?? []).map(platformLabel).join(' and ');
  const lines = [
    `${plural(estimate.caseCount ?? 0, 'case')} on ${platforms || 'no platform'}, ` +
    `${plural(estimate.repeats ?? 1, 'repeat')}: ${plural(estimate.plannedCount ?? 0, 'answer')}`,
    `Bot ${estimate.botModel}: ${usd(estimate.botPerCaseUsd)} an answer`,
    `Judge ${estimate.judgeModel}: ${usd(estimate.judgePerCaseUsd)} an answer`,
  ];
  if (estimate.warmupUsd != null) lines.push(`Cache warm up: ${usd(estimate.warmupUsd)}`);
  lines.push(`Spent today ${usd(estimate.spentTodayUsd ?? 0)} of ${usd(estimate.maxDailySpendUsd ?? 0)}, ` +
      `this month ${usd(estimate.spentThisMonthUsd ?? 0)} of ${usd(estimate.maxMonthlySpendUsd ?? 0)}`);
  lines.push(`A run stops at ${usd(estimate.maxRunSpendUsd ?? 0)}`);
  return {
    headline: estimate.estimatedUsd == null ? 'Cost unknown' : `About ${usd(estimate.estimatedUsd)}`,
    lines,
    warnings: estimate.warnings ?? [],
    refusal: estimate.refusal ?? null,
    canStart: estimate.canStart === true,
  };
}

/** An estimate only authorises Start for the exact request it was worked out for. */
export function estimateIsCurrent(request, estimatedFor) {
  return estimatedFor != null && JSON.stringify(request) === estimatedFor;
}

export function environmentName(apiBaseUrl) {
  if (!apiBaseUrl) return 'this environment';
  let host;
  try {
    host = new URL(apiBaseUrl).hostname.toLowerCase();
  } catch {
    return 'this environment';
  }
  if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') return 'Local';
  if (host.startsWith('devapi.')) return 'DEV';
  if (host.startsWith('uatapi.')) return 'UAT';
  if (host === 'api.tapleagues.com') return 'Production';
  return host;
}

/** The Start button says where it runs and how much, so nobody starts a UAT run thinking it is local. */
export function startButtonLabel({environment, caseCount, repeats}) {
  if (caseCount == null) return `Start on ${environment}`;
  const what = plural(caseCount, 'case');
  return repeats > 1 ? `Start ${what}, ${repeats} repeats, on ${environment}` : `Start ${what} on ${environment}`;
}

// ---- availability and errors ----------------------------------------------------------

/** The useState key holding whether the console is on (useAssistantEvalsNav fills it). */
export const EVALS_ENABLED_STATE = 'assistantEvalsEnabled';

/** The nav item shows only when the API says the console is on. */
export function evalsNavVisible(status) {
  return status?.enabled === true;
}

/** httpRequest throws the raw Response (status, no body) or the parsed body (global, no status). */
export function errorStatus(e) {
  return typeof e?.status === 'number' ? e.status : null;
}

export function errorMessage(e, fallback) {
  return (Array.isArray(e?.global) && e.global[0]) || fallback;
}

/** 'on', 'off' (switched off: a 404, or enabled false), 'denied' or 'error'. */
export function evalsAvailability({status, error} = {}) {
  if (error) {
    const code = errorStatus(error);
    if (code === 404) return 'off';
    if (code === 401 || code === 403) return 'denied';
    return 'error';
  }
  return evalsNavVisible(status) ? 'on' : 'off';
}

/** A 204 has no body, and httpRequest always parses one: the SyntaxError IS the success. */
export function isEmptyBodySuccess(e) {
  return e instanceof SyntaxError;
}

// ---- pasting and copying --------------------------------------------------------------

/**
 * A paste is sent to the API exactly as parsed: an array, an object with a `listKey` list
 * (cases.json itself), or one entry. This only refuses what the API could not read at all.
 */
export function parsePaste(text, listKey = 'cases') {
  const trimmed = String(text ?? '').trim();
  if (!trimmed) return {ok: false, error: 'Paste some JSON first.'};
  let value;
  try {
    value = JSON.parse(trimmed);
  } catch (e) {
    return {ok: false, error: `That is not valid JSON: ${e?.message ?? 'it could not be read'}`};
  }
  let count;
  if (Array.isArray(value)) {
    count = value.length;
  } else if (value && typeof value === 'object') {
    if (listKey in value) {
      if (!Array.isArray(value[listKey])) return {ok: false, error: `"${listKey}" must be a list.`};
      count = value[listKey].length;
    } else {
      count = 1;
    }
  } else {
    return {ok: false, error: `Paste a JSON array, an object with a "${listKey}" list, or one ${listKey === 'items' ? 'item' : 'case'}.`};
  }
  if (count === 0) return {ok: false, error: 'The paste has nothing in it.'};
  return {ok: true, value, count};
}

const CHANGE_LABELS = {new: 'new', changed: 'changed', unchanged: 'unchanged', invalid: 'invalid'};

/** A dry run's verdict, and whether Save may be pressed: something to save, and nothing invalid. */
export function previewSummary(preview) {
  if (!preview) return null;
  const counts = [
    {key: 'new', label: 'New', count: preview.new ?? 0},
    {key: 'changed', label: 'Changed', count: preview.changed ?? 0},
    {key: 'unchanged', label: 'Unchanged', count: preview.unchanged ?? 0},
    {key: 'invalid', label: 'Invalid', count: preview.invalid ?? 0},
  ];
  const items = (preview.items ?? []).map(item => {
    const status = String(item.status ?? '').toLowerCase();
    const name = item.id || `Entry ${(item.index ?? 0) + 1}`;
    let text = `${name}: ${CHANGE_LABELS[status] ?? status}`;
    if (status === 'changed' && item.changedFields?.length) text += ` (${item.changedFields.join(', ')})`;
    if (status === 'invalid' && item.reason) text += `, ${item.reason}`;
    return {key: `${item.index}:${item.id ?? ''}`, status, text};
  });
  return {
    counts,
    items,
    canSave: (preview.invalid ?? 0) === 0 && ((preview.new ?? 0) + (preview.changed ?? 0)) > 0,
  };
}

/** The cases.json field order, so a copy reads like the repo file. */
const CASE_FIELDS = ['id', 'platform', 'kind', 'question', 'turns', 'expectations', 'must_contain',
  'must_not_contain', 'max_words', 'docs', 'note', 'trial_group'];

/** A case in the cases.json shape: what pastes straight back, here or into the repo. */
export function caseForCopy(c) {
  const out = {};
  for (const field of CASE_FIELDS) {
    if (c?.[field] != null) out[field] = c[field];
  }
  return out;
}

/** One case copies as itself, several as {"cases": [...]}; the paste reads both. */
export function casesCopyText(cases) {
  const list = (cases ?? []).map(caseForCopy);
  return JSON.stringify(list.length === 1 ? list[0] : {cases: list}, null, 2);
}

/** A stored calibration item flattened to the judge-calibration.json shape the paste reads. */
export function judgeItemForCopy(listed) {
  const item = listed?.item ?? {};
  const out = caseForCopy(item.case);
  const extra = {
    answer: item.answer,
    expected_verdict: item.expectedVerdict,
    difficulty: item.difficulty,
    transcript: item.transcript,
    reference_docs: item.referenceDocs,
  };
  for (const [key, value] of Object.entries(extra)) {
    if (value != null) out[key] = value;
  }
  return out;
}

export function judgeItemsCopyText(items) {
  const list = (items ?? []).map(judgeItemForCopy);
  return JSON.stringify(list.length === 1 ? list[0] : {items: list}, null, 2);
}

export function analysisText(analysis) {
  return analysis?.markdown ?? '';
}


export function docsPassText(findings) {
  return findings?.copyForDocsPass?.trim() ? findings.copyForDocsPass : 'No docs findings in this run.';
}

// ---- cases ----------------------------------------------------------------------------

/**
 * The cases the filters leave. group: undefined shows every case, null the suite only (no trial
 * group), a name that trial group only.
 */
export function filterCases(cases, {search = '', kind = null, platform = null, group = undefined} = {}) {
  const needle = String(search ?? '').trim().toLowerCase();
  return (cases ?? []).filter(c => {
    if (group !== undefined && (c.trial_group ?? null) !== group) return false;
    if (kind && c.kind !== kind) return false;
    if (platform && c.platform !== platform) return false;
    if (!needle) return true;
    const haystack = [c.id, c.question, ...(c.turns ?? []), c.expectations, c.note, ...(c.docs ?? [])]
        .filter(Boolean).join('\n').toLowerCase();
    return haystack.includes(needle);
  });
}

// ---- trial groups ---------------------------------------------------------------------

/** The Cases tab's chips: the suite first, then each trial group in the order its cases are listed. */
export function trialGroupChips(cases) {
  const list = cases ?? [];
  const chips = [{value: null, label: 'Suite', count: list.filter(c => !c.trial_group).length}];
  for (const c of list) {
    if (!c.trial_group) continue;
    const chip = chips.find(x => x.value === c.trial_group);
    if (chip) chip.count++;
    else chips.push({value: c.trial_group, label: c.trial_group, count: 1});
  }
  return chips;
}

const dayMonth = (date) => {
  const when = new Date(date);
  return Number.isNaN(when.getTime()) ? '' : when.toLocaleDateString('en-GB', {day: 'numeric', month: 'short', timeZone: 'UTC'});
};

/** "6 cases, started 8 Oct". */
export function trialGroupLine(group) {
  const started = dayMonth(group?.startedAt);
  return plural(group?.caseCount ?? 0, 'case') + (started ? `, started ${started}` : '');
}

/** How the group's last run went, in a line. */
export function trialGroupLastRunLine(run) {
  if (!run) return 'Not run yet.';
  if (isRunActive(run.status)) return `Running now, ${run.completedCount ?? 0} of ${run.plannedCount ?? 0} done.`;
  const when = dayMonth(run.createdAt);
  const score = run.score == null ? runStatusLabel(run.status).toLowerCase() : `${percent(run.score)} passed`;
  return `Last run ${when}: ${score}, ${usd(run.totalCostUsd)}.`;
}

// ---- expectations in three parts ------------------------------------------------------

const PART_LABELS = [['required', 'Required:'], ['failsIf', 'Fails if:'], ['fineToLeaveOut', 'Fine to leave out:']];

/**
 * An expectation read as its three parts when it is written that way ("Required: ... Fails if: ...
 * Fine to leave out: ..."), otherwise kept whole as plain text. Only the first two decide a case.
 */
export function splitExpectations(text) {
  const raw = String(text ?? '');
  const out = {parts: false, required: '', failsIf: '', fineToLeaveOut: '', plain: ''};
  const marks = PART_LABELS
      .map(([key, label]) => ({key, label, at: raw.search(new RegExp('(^|\\s)' + label.replace(/ /g, '\\s+')))}))
      .filter(m => m.at >= 0)
      .sort((a, b) => a.at - b.at);
  if (!marks.length || !/^\s*Required:/.test(raw)) return {...out, plain: raw.trim()};
  marks.forEach((m, i) => {
    const start = raw.indexOf(m.label, m.at) + m.label.length;
    const end = i + 1 < marks.length ? marks[i + 1].at : raw.length;
    out[m.key] = raw.slice(start, end).trim();
  });
  return {...out, parts: true};
}

/** The three parts back as one expectation, one part to a line, empty parts left out. */
export function joinExpectations({required = '', failsIf = '', fineToLeaveOut = ''} = {}) {
  const values = {required, failsIf, fineToLeaveOut};
  return PART_LABELS
      .map(([key, label]) => [label, String(values[key] ?? '').trim()])
      .filter(([, value]) => value)
      .map(([label, value]) => `${label} ${value}`)
      .join('\n');
}

export function caseKinds(cases) {
  return [...new Set((cases ?? []).map(c => c.kind).filter(Boolean))].sort();
}

/** A blank case for the editor, in the shape the API validates. */
export function emptyCase() {
  return {id: '', platform: 'app', kind: 'howto', question: '', expectations: '', docs: []};
}

// ---- the run request ------------------------------------------------------------------

const platformsOf = (platforms) => (platforms === 'app' ? ['app'] : platforms === 'web' ? ['web'] : ['app', 'web']);

const clampRepeats = (n) => Math.min(5, Math.max(1, Math.round(Number(n) || 1)));

const PRICE_FIELDS = ['inputUsdPerMTok', 'outputUsdPerMTok', 'cacheReadUsdPerMTok', 'cacheWriteUsdPerMTok'];

const docsSourceRequest = (source) => {
  const kind = source?.kind ?? 'published';
  if (kind === 'published' || kind === 'prod' || kind === 'latest' || kind === 'local') return {kind};
  return {kind, ref: String(source?.ref ?? '').trim()};
};

/** Every case id the affected endpoints named (changed docs, plus untagged cases, which always run), once each. */
export function affectedCaseIds(affected) {
  const ids = [];
  for (const platform of affected ?? []) {
    for (const id of [...(platform.affectedCaseIds ?? []), ...(platform.untaggedCaseIds ?? [])]) {
      if (!ids.includes(id)) ids.push(id);
    }
  }
  return ids;
}

/** The form, as the API's AssistantEvalRunRequest. */
export function buildRunRequest(form, {liveBot} = {}) {
  let bot = null;
  if (form.botMode === 'custom') {
    bot = {...(liveBot ?? {}), ...form.bot, modelName: String(form.bot?.modelName ?? '').trim()};
    for (const field of PRICE_FIELDS) bot[field] = Number(bot[field] ?? 0) || 0;
  }

  let cases;
  const selection = form.selection ?? {mode: 'all'};
  if (selection.mode === 'ids') cases = {mode: 'ids', caseIds: [...(selection.caseIds ?? [])]};
  else if (selection.mode === 'failedOrFlakyInRun') cases = {mode: 'failedOrFlakyInRun', runId: selection.runId};
  else if (selection.mode === 'differ') cases = {mode: 'ids', caseIds: affectedCaseIds(form.affected)};
  else if (selection.mode === 'trialGroup') cases = {mode: 'trialGroup', trialGroup: selection.trialGroup};
  else cases = {mode: 'all'};

  const concurrency = form.concurrency === '' || form.concurrency == null ? null : Math.round(Number(form.concurrency));
  const judge = String(form.judgeDeployment ?? '').trim();
  const label = String(form.label ?? '').trim();

  return {
    bot,
    judgeDeployment: judge || null,
    // A platform the run leaves out is sent as published, so a half typed branch there cannot refuse the run.
    docs: {
      app: platformsOf(form.platforms).includes('app') ? docsSourceRequest(form.docs?.app) : {kind: 'published'},
      web: platformsOf(form.platforms).includes('web') ? docsSourceRequest(form.docs?.web) : {kind: 'published'},
    },
    platforms: form.platforms ?? 'both',
    cases,
    repeats: clampRepeats(form.repeats),
    concurrency: Number.isFinite(concurrency) && concurrency > 0 ? concurrency : null,
    compareWith: form.compareWith || null,
    label: label || null,
  };
}

/** What is missing before the request is worth sending, in words. */
export function runRequestProblems(form) {
  const problems = [];
  if (form.botMode === 'custom' && !String(form.bot?.modelName ?? '').trim()) {
    problems.push('Name the bot model or deployment.');
  }
  for (const platform of platformsOf(form.platforms)) {
    const source = form.docs?.[platform];
    if (source && (source.kind === 'branch' || source.kind === 'commit') && !String(source.ref ?? '').trim()) {
      problems.push(`Name the ${platformLabel(platform)} docs ${source.kind === 'commit' ? 'commit' : 'branch'}.`);
    }
  }
  const selection = form.selection ?? {mode: 'all'};
  if (selection.mode === 'ids' && !(selection.caseIds ?? []).length) problems.push('Choose at least one case.');
  if (selection.mode === 'failedOrFlakyInRun' && !selection.runId) {
    problems.push('Choose the run whose failed or flaky cases to run again.');
  }
  if (selection.mode === 'trialGroup' && !String(selection.trialGroup ?? '').trim()) problems.push('Choose a trial group.');
  if (selection.mode === 'differ') {
    if (form.affected == null) problems.push('Waiting for prod and UAT to be compared.');
    else if (!affectedCaseIds(form.affected).length) problems.push('No case tests a doc that differs between prod and UAT.');
  }
  return problems;
}

export function affectedView(affected) {
  return (affected ?? []).map(a => {
    const changed = a.changedDocKeys ?? [];
    const cases = a.affectedCaseIds ?? [];
    const untagged = a.untaggedCaseIds ?? [];
    const short = (sha) => (sha ? String(sha).slice(0, 7) : 'unknown');
    // A local docs folder has no commit.
    return {
      platform: a.platform,
      title: `${platformLabel(a.platform)}: ${short(a.baseSha)} to ${a.targetSha ? short(a.targetSha) : 'this machine'}`,
      summary: `${plural(changed.length, 'doc')} changed, ${plural(cases.length, 'case')} ` +
          `${cases.length === 1 ? 'tests' : 'test'} them, ${plural(untagged.length, 'untagged case')} always run`,
      changedDocKeys: changed,
      uncoveredChangedDocKeys: a.uncoveredChangedDocKeys ?? [],
    };
  });
}

/** The newest finished run on the live bot model: what a new run is compared with by default. */
export function defaultCompareWith(runs, liveBot) {
  const match = (runs ?? []).find(r =>
      String(r.kind ?? 'Suite').toLowerCase() === 'suite'
      && normaliseStatus(r.status) === 'Completed'
      && r.botModel === liveBot?.modelName);
  return match?.id ?? null;
}

// ---- compare, judge agreement, coverage -----------------------------------------------

const compareCell = (value) => (VERDICT_LABELS[String(value ?? '').toLowerCase()] ?? (value || 'missing'));

const seconds = (n) => (n == null ? 'unknown' : `${Math.round(Number(n) * 10) / 10} s`);

/** Changed verdicts first (the API already orders them; kept here so the page cannot lose it). */
export function compareRows(compare) {
  const rows = (compare?.rows ?? []).map(r => ({
    key: `${r.platform}:${r.caseId}`,
    caseId: r.caseId,
    platform: r.platform,
    a: compareCell(r.a),
    b: compareCell(r.b),
    changed: r.changed === true,
    cost: `${usd(r.costAUsd)} / ${usd(r.costBUsd)}`,
    seconds: `${seconds(r.secondsA)} / ${seconds(r.secondsB)}`,
  }));
  return [...rows.filter(r => r.changed), ...rows.filter(r => !r.changed)];
}

export function agreementLines(agreement) {
  if (!agreement) return [];
  const line = (label, agreed, total) => ({
    label,
    text: total ? `${agreed} of ${total} (${percent(agreed / total)})` : 'none',
    all: total > 0 && agreed === total,
  });
  return [
    line('Overall', agreement.agreed ?? 0, agreement.total ?? 0),
    line('Clear', agreement.clearAgreed ?? 0, agreement.clearTotal ?? 0),
    line('Hard', agreement.hardAgreed ?? 0, agreement.hardTotal ?? 0),
  ];
}

export function coverageView(coverage) {
  if (!coverage) return null;
  const docs = coverage.docs ?? [];
  const covered = docs.filter(d => (d.caseIds ?? []).length > 0).length;
  const sha = coverage.sha ? ` at ${String(coverage.sha).slice(0, 7)}` : '';
  const titles = Object.fromEntries(docs.map(d => [d.key, d.title]));
  return {
    heading: `${platformLabel(coverage.platform)} docs, ${coverage.sourceDescription ?? 'published'}${sha}: ` +
        `${plural(docs.length, 'doc')}, ${covered} covered by a case`,
    uncovered: (coverage.uncoveredDocKeys ?? []).map(key => ({key, title: titles[key] ?? key})),
    unknownTags: (coverage.unknownTags ?? []).map(t => `${t.caseId} tags ${t.docKey}, which is not in these docs`),
    docs: docs.map(d => ({key: d.key, title: d.title, cases: (d.caseIds ?? []).length})),
  };
}

// ---- the case editor ------------------------------------------------------------------

/** A case as the side panel edits it: every list present, the question or the turns. */
export function editorFromCase(c) {
  const turns = c?.turns?.length ? [...c.turns] : [''];
  return {
    id: c?.id ?? '',
    platform: c?.platform ?? 'app',
    kind: c?.kind ?? 'howto',
    multiTurn: !!c?.turns?.length,
    question: c?.question ?? '',
    turns,
    expectations: c?.expectations ?? '',
    must_contain: [...(c?.must_contain ?? [])],
    must_not_contain: [...(c?.must_not_contain ?? [])],
    max_words: c?.max_words ?? '',
    docs: [...(c?.docs ?? [])],
    note: c?.note ?? '',
    trial_group: c?.trial_group ?? '',
    // The expectation as the editor shows it: three boxes, or one for an old one-paragraph expectation.
    exp: splitExpectations(c?.expectations ?? ''),
  };
}

/** The editor back as a case to PUT: empty fields left out, so an untouched case reads unchanged. */
export function caseFromEditor(editor) {
  const trimmed = (v) => String(v ?? '').trim();
  const list = (values) => (values ?? []).map(trimmed).filter(Boolean);
  const out = {
    id: trimmed(editor.id),
    platform: editor.platform,
    kind: trimmed(editor.kind),
  };
  if (editor.multiTurn) out.turns = list(editor.turns);
  else out.question = trimmed(editor.question);
  // The three boxes win when the editor shows them; an old plain expectation saves exactly as written.
  out.expectations = editor.exp
      ? (editor.exp.parts ? joinExpectations(editor.exp) : trimmed(editor.exp.plain))
      : trimmed(editor.expectations);
  const mustContain = list(editor.must_contain);
  if (mustContain.length) out.must_contain = mustContain;
  const mustNotContain = list(editor.must_not_contain);
  if (mustNotContain.length) out.must_not_contain = mustNotContain;
  if (editor.max_words !== '' && editor.max_words != null && Number.isFinite(Number(editor.max_words))) {
    out.max_words = Math.round(Number(editor.max_words));
  }
  const docs = list(editor.docs);
  if (docs.length) out.docs = docs;
  if (trimmed(editor.note)) out.note = trimmed(editor.note);
  if (trimmed(editor.trial_group)) out.trial_group = trimmed(editor.trial_group);
  return caseForCopy(out);
}

// ---- dates -----------------------------------------------------------------------------

/** "5 Oct 2026": the month named, so the order of day and month cannot be misread. */
export function refDateLabel(date) {
  if (!date) return '';
  const when = new Date(date);
  if (Number.isNaN(when.getTime())) return '';
  return when.toLocaleDateString('en-GB', {day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'});
}
